// app/restaurant/page.tsx
"use client";

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useRouter } from "next/navigation";
import { 
  Search, 
  Filter,  
  Loader2, 
  Grid3x3,
  List,
  Navigation
} from "lucide-react";
import apiClient from "@/lib/api/apiClient";
import { InteractiveRestaurantCard } from "@/components/restaurant/InteractiveRestaurantCard";
import { FiltersSidebar } from "@/components/restaurant/FiltersSidebar"; 
import { useVendor } from "@/context/VendorContext";
interface Vendor {
  id: number;
  vendor_code: string;
  name: string;
  slug: string;
  description: string | null;
  address: VendorAddress | null;
  contact_email: string;
  contact_phone: string;
  status: 'active' | 'pending' | 'inactive' | 'suspended';
  is_verified: boolean;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
  detail: VendorDetail;
  average_rating?: number;  
  owner_id: number;
  created_by: number | null;
  verification_status: VendorVerificationStatus;
  user_role: string;
}

interface VendorAddress {
  street: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
  latitude?: number;
  longitude?: number;
}

interface VendorVerificationStatus {
  is_verified: boolean;
  verified_at: string | null;
  verified_by: number | null;
}

interface VendorDetail {
  business_type: string | null;
  price_range: string;
  cuisine_types: string[];
  features: string[];
  amenities: string[];
  tags: string[];
  logo_url: string | null;
  cover_url: string | null;
  average_rating: number;
  total_reviews: number;
}
 
interface FilterState {
  search: string;
  priceRange: string[];
  distance: number;
  cuisines: string[];
  rating: number;
  deliveryTime: number;
  openNow: boolean;
  offers: boolean;
  features: string[];
  amenities: string[];
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}
interface VendorFilters {
  search?: string;
  cuisine?: string;
  price_range?: string;
  min_rating?: number;
  status?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
}

export default function RestaurantBrowsingPage() {
  const router = useRouter();
  const { selectedVendor, setSelectedVendor, hasCartConflict } = useVendor();
  
  // State
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  
  // Filters
  const [filters, setFilters] = useState<FilterState>({
    search: "",
    priceRange: [],
    distance: 50,
    cuisines: [],
    rating: 0,
    deliveryTime: 60,
    openNow: false,
    offers: false,
    features: [],
    amenities: [],
    sortBy: "rating",
    sortOrder: "desc",
  });
  
  // Available options
  const [availableCuisines, setAvailableCuisines] = useState<string[]>([]);
  const [availablePriceRanges, setAvailablePriceRanges] = useState<string[]>([
    "$", "$$", "$$$", "$$$$"
  ]);
  const [availableFeatures, setAvailableFeatures] = useState<string[]>([
    "Outdoor Seating",
    "Live Music",
    "Family Friendly",
    "Romantic",
    "Business Meetings",
    "Pet Friendly"
  ]);
  const [availableAmenities, setAvailableAmenities] = useState<string[]>([
    "WiFi",
    "Parking",
    "Wheelchair Accessible",
    "Alcohol Served",
    "Takeout Available",
    "Reservations"
  ]);
  
  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 12,
  });

  // Refs
  const searchTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const filtersRef = useRef<FilterState>(filters);

  // Initialize available options
  const initializeOptions = useCallback(async () => {
    try {
      // Temporarily mock the cuisines until the backend methods are implemented
      setAvailableCuisines([
        "Italian", "American", "Mexican", "Asian", "Indian", "Mediterranean", "Fast Food", "Vegan", "Desserts"
      ]);
    } catch (err) {
      console.error("Error loading options:", err);
    }
  }, []);

  // Fetch vendors with filters
  const fetchVendors = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const apiFilters: VendorFilters = {
        page: pagination.currentPage,
        per_page: pagination.itemsPerPage,
      };

      // Apply filters
      if (filters.search) {
        apiFilters.search = filters.search;
      }
      
      if (filters.cuisines.length > 0) {
        apiFilters.cuisine = filters.cuisines.join(',');
      }
      
      if (filters.priceRange.length > 0) {
        apiFilters.price_range = filters.priceRange.join(',');
      }
      
      if (filters.rating > 0) {
        apiFilters.min_rating = filters.rating;
      }
      
      if (filters.openNow) {
        apiFilters.status = 'active';
      }
      
      if (filters.sortBy) {
        apiFilters.sort_by = filters.sortBy;
        apiFilters.sort_order = filters.sortOrder;
      }

      const res = await apiClient.get<any>('/vendors', { params: apiFilters });
      if (res.status === 'success' && res.data) {
        const data = res.data;
        setVendors(data.vendors || []);
        
        if (data.pagination) {
          setPagination(prev => ({
            ...prev,
            currentPage: data.pagination?.current_page || 1,
            totalPages: data.pagination?.last_page || 1,
            totalItems: data.pagination?.total || 0,
          }));
        }
      } else if (Array.isArray(res)) {
        // Fallback for raw arrays
        setVendors(res);
      } else {
        throw new Error(res.message || 'Failed to fetch restaurants');
      }
    } catch (err) {
      console.error("Error fetching vendors:", err);
      setError(err instanceof Error ? err.message : "An error occurred");
      setVendors([]);
    } finally {
      setIsLoading(false);
      setIsInitialLoad(false);
    }
  }, [filters, pagination.currentPage, pagination.itemsPerPage]);

  // Handle filter changes with debounce for search
  const handleFilterChange = useCallback((newFilters: FilterState) => {
    filtersRef.current = newFilters;
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      setFilters(newFilters);
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }, newFilters.search ? 500 : 100);
  }, []);

  // Handle restaurant selection
  const handleRestaurantSelect = useCallback((vendor: Vendor) => {
    if (hasCartConflict(vendor.id)) {
      const shouldProceed = window.confirm(
        'You have items from another restaurant in your cart. ' +
        'Viewing this restaurant\'s menu will clear your current cart. Continue?'
      );
      
      if (!shouldProceed) {
        return;
      }
    }
    
    setSelectedVendor(vendor);
    router.push(`/vendors/${vendor.id}/menu`);
  }, [hasCartConflict, router, setSelectedVendor]);

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    const cleared = {
      search: "",
      priceRange: [],
      distance: 50,
      cuisines: [],
      rating: 0,
      deliveryTime: 60,
      openNow: false,
      offers: false,
      features: [],
      amenities: [],
      sortBy: "rating",
      sortOrder: "desc" as const,
    };
    setFilters(cleared);
    filtersRef.current = cleared;
  }, []);

  // Handle search input change
  const handleSearchChange = useCallback((value: string) => {
    handleFilterChange({ ...filtersRef.current, search: value });
  }, [handleFilterChange]);

  // Initialize and fetch data
  useEffect(() => {
    const initialize = async () => {
      await initializeOptions();
      await fetchVendors();
    };
    initialize();
  }, [initializeOptions, fetchVendors]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Memoized empty state
  const emptyState = useMemo(() => ({
    title: "No restaurants found",
    description: "Try adjusting your filters to find more dining options",
    action: {
      label: "Clear All Filters",
      onClick: handleClearFilters,
    },
  }), [handleClearFilters]);

  const activeFilterCount = useMemo(() => {
    return Object.entries(filters).reduce((count, [key, value]) => {
      if (key === 'search' && value) return count + 1;
      if (Array.isArray(value) && value.length > 0) return count + 1;
      if (typeof value === 'number' && (key === 'distance' || key === 'deliveryTime') && value !== (key === 'distance' ? 50 : 60)) return count + 1;
      if (typeof value === 'number' && key === 'rating' && value > 0) return count + 1;
      if (typeof value === 'boolean' && value) return count + 1;
      return count;
    }, 0);
  }, [filters]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-gray-900">Discover Restaurants</h1>
              <p className="text-gray-600 mt-2">
                Select a restaurant to view their exclusive menu and start ordering
              </p>
            </div>
            
            {/* Main Search */}
            <div className="relative max-w-2xl">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                defaultValue={filters.search}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search restaurants, cuisines, or locations..."
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar - Desktop */}
          <div className="hidden lg:block lg:w-80 flex-shrink-0">
            <FiltersSidebar
              filters={filters}
              onFilterChange={handleFilterChange}
              availableCuisines={availableCuisines}
              availablePriceRanges={availablePriceRanges}
              availableFeatures={availableFeatures}
              availableAmenities={availableAmenities}
            />
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Mobile Filter Button */}
            <div className="lg:hidden mb-6">
              <button
                onClick={() => setShowMobileFilters(true)}
                className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 w-full"
              >
                <Filter className="w-4 h-4" />
                <span className="font-medium">Filters & Sort</span>
                {activeFilterCount > 0 && (
                  <span className="ml-auto px-2 py-1 bg-amber-500 text-white text-sm rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>

            {/* Results Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {isLoading ? 'Loading...' : `${pagination.totalItems} Restaurants Available`}
                </h2>
                {!isLoading && (
                  <p className="text-sm text-gray-600 mt-1">
                    Showing {vendors.length} of {pagination.totalItems} results
                    {filters.sortBy && ` • Sorted by ${filters.sortBy.replace('_', ' ')}`}
                  </p>
                )}
              </div>
              
              <div className="flex items-center gap-3">
                {/* View Mode Toggle */}
                <div className="hidden sm:flex items-center bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`p-2 rounded transition-colors ${
                      viewMode === "grid" 
                        ? "bg-white shadow-sm text-gray-900" 
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                    aria-label="Grid view"
                  >
                    <Grid3x3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`p-2 rounded transition-colors ${
                      viewMode === "list" 
                        ? "bg-white shadow-sm text-gray-900" 
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                    aria-label="List view"
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Sort Menu (Mobile) */}
                <div className="lg:hidden">
                  <button
                    onClick={() => setShowMobileFilters(true)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                  >
                    Sort
                  </button>
                </div>
              </div>
            </div>

            {/* Restaurants Grid */}
            {isLoading && isInitialLoad ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="w-12 h-12 text-amber-500 animate-spin mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Loading restaurants...</h3>
                <p className="text-gray-600">Finding the best dining options for you</p>
              </div>
            ) : error ? (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                  <Navigation className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load restaurants</h3>
                <p className="text-gray-600 mb-6">{error}</p>
                <button
                  onClick={fetchVendors}
                  className="px-6 py-3 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-colors"
                >
                  Try Again
                </button>
              </div>
            ) : vendors.length > 0 ? (
              <div className={viewMode === "grid" 
                ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                : "space-y-6"
              }>
                {vendors.map((vendor) => (
                  <InteractiveRestaurantCard
                    key={vendor.id}
                    vendor={vendor}
                    onSelect={handleRestaurantSelect}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
                  <Search className="w-8 h-8 text-amber-500" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {emptyState.title}
                </h3>
                <p className="text-gray-600 mb-6">{emptyState.description}</p>
                <button
                  onClick={emptyState.action.onClick}
                  className="px-6 py-3 bg-amber-500 text-white font-semibold rounded-lg hover:bg-amber-600 transition-colors"
                >
                  {emptyState.action.label}
                </button>
              </div>
            )}

            {/* Pagination */}
            {!isLoading && !error && vendors.length > 0 && pagination.totalPages > 1 && (
              <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm text-gray-600">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPagination(prev => ({ 
                      ...prev, 
                      currentPage: Math.max(1, prev.currentPage - 1) 
                    }))}
                    disabled={pagination.currentPage === 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    Previous
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      let pageNum = i + 1;
                      if (pagination.totalPages > 5) {
                        if (pagination.currentPage > 3) {
                          pageNum = pagination.currentPage - 3 + i;
                          if (pageNum > pagination.totalPages - 2) {
                            pageNum = pagination.totalPages - 4 + i;
                          }
                        }
                      }
                      return pageNum;
                    })
                    .filter(page => page >= 1 && page <= pagination.totalPages)
                    .map(page => (
                      <button
                        key={page}
                        onClick={() => setPagination(prev => ({ ...prev, currentPage: page }))}
                        className={`w-10 h-10 rounded-lg transition-colors ${
                          pagination.currentPage === page
                            ? "bg-amber-500 text-white"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                  
                  <button
                    onClick={() => setPagination(prev => ({ 
                      ...prev, 
                      currentPage: Math.min(pagination.totalPages, prev.currentPage + 1) 
                    }))}
                    disabled={pagination.currentPage === pagination.totalPages}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filters Modal */}
      {showMobileFilters && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowMobileFilters(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-sm bg-white shadow-xl overflow-y-auto">
            <FiltersSidebar
              filters={filters}
              onFilterChange={handleFilterChange}
              availableCuisines={availableCuisines}
              availablePriceRanges={availablePriceRanges}
              availableFeatures={availableFeatures}
              availableAmenities={availableAmenities}
              isMobile={true}
              onClose={() => setShowMobileFilters(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}